// Two-axis Renewal Board grid — FE-SCRM-008.
//
// Columns are months; rows are pipeline stages. Per-cell rendering
// switches between Card mode (1-3) and Count mode (4+) using
// ``card_render_hint`` from the BE; both modes render at identical
// cell heights so the grid geometry, row alignment, and the diagonal
// cadence pattern stay intact.
//
// The closed_won / closed_lost summary row that used to render below
// the active stages is removed (FE-SCRM-064c) — the toolbar's "Show
// closed" toggle already surfaces those stages in the main grid when
// the producer wants them, and the bottom summary block was confusing
// against an otherwise active-stages-only view.
//
// Column headers (the month labels) are ``sticky top-0`` so they stay
// visible as the producer scrolls down through the stage rows.
//
// Drop targets: each cell accepts vertical drops to change stage
// and horizontal drops to change renewal_month. The horizontal
// case opens a confirmation modal in ``RenewalBoardPanel`` before
// persisting.

import { useMemo } from "react";

import { CountTile } from "./CountTile";
import { OpportunityCard } from "./OpportunityCard";
import type {
  OpportunityStage,
  RenewalBoardResponse,
} from "./types";


const STAGE_LABEL: Record<OpportunityStage, string> = {
  lead: "Lead",
  meeting_set: "Meeting set",
  qualified: "Qualified",
  proposal: "Proposal",
  negotiation: "Negotiation",
  bor_signed_pending_effective_date: "BOR signed",
  closed_won: "Closed won",
  closed_lost: "Closed lost",
};


const MONTH_NAME = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];


interface Props {
  board: RenewalBoardResponse;
  onOpportunityClick: (opportunity_id: string) => void;
  onCountTileClick: (target: { month: string; stage: string }) => void;
  onDrop: (params: {
    opportunity_id: string;
    from: { stage: string; renewal_year: number; renewal_month: number };
    to: { stage: string; renewal_year: number; renewal_month: number };
  }) => void;
}


export function Grid({
  board, onOpportunityClick, onCountTileClick, onDrop,
}: Props) {
  // Stage rows in the active grid (closed_won/lost render below).
  const activeStages: OpportunityStage[] = useMemo(() => {
    if (!board.columns.length) return [];
    return board.columns[0].cells.map((c) => c.stage);
  }, [board]);

  const today = useMemo(() => board.today, [board.today]);
  const todayKey = today.slice(0, 7); // "YYYY-MM"

  return (
    <div
      data-testid="renewal-board-grid"
      // Bounded height + ``overflow-auto`` makes this wrapper the
      // scroll container for BOTH axes. Without a bound, vertical
      // scrolling happens on ``dashboard-main`` above us — sticky
      // headers inside the wrapper would track the wrapper's top
      // (moving with the page) rather than the viewport, and the
      // month-axis ribbon disappeared as the producer scrolled
      // down. With the bound, sticky-top sticks to the wrapper's
      // top edge (which stays put) and sticky-left can lock the
      // lead column during horizontal scroll. The ``180px`` offset
      // approximates page chrome (single-line title + tabs) +
      // toolbar above the grid; tight enough that the grid fills
      // remaining viewport space without bleeding past it.
      className="h-[calc(100vh-180px)] overflow-auto"
    >
      <div
        className="grid min-w-fit"
        style={{
          gridTemplateColumns:
            `120px repeat(${board.columns.length}, minmax(140px, 1fr))`,
        }}
      >
        {/* Header row: empty corner + month labels. Each cell is
            ``sticky top-0`` so the month axis stays visible as the
            producer scrolls down through stage rows. The lead
            column (row headers) is ``sticky left-0`` so the stage
            axis stays visible during horizontal scroll. The corner
            cell sits at the top-left intersection of both sticky
            tracks and gets ``z-30`` so it overlaps both ribbons
            cleanly when scrolled in either direction. ``bg-canvas``
            on every sticky cell hides body cards sliding underneath.

            Hairline gridlines (``border-border-subtle``) on the right
            and bottom of every cell mirror the pencil redesigned frame
            — gap is removed from the grid so the 1px rules connect
            into a continuous table-style ruling. */}
        <div
          data-testid="grid-corner"
          className="sticky top-0 left-0 z-30 bg-canvas border-r border-b border-border-subtle"
        />
        {board.columns.map((col, colIdx) => {
          const isToday = col.key === todayKey;
          const isLastCol = colIdx === board.columns.length - 1;
          return (
            <div
              key={col.key}
              className={[
                "sticky top-0 z-20 bg-canvas",
                "relative text-[11px] uppercase tracking-wide font-medium",
                "text-text-tertiary px-2 py-1.5 text-center",
                "border-b border-border-subtle",
                isLastCol ? "" : "border-r border-border-subtle",
                // Current-month emphasis: the recessed background tint
                // is the only signal now. The pencil design omits the
                // floating "TODAY" pill entirely — it overlapped the
                // cadence-health tooltips on the cards below.
                isToday ? "bg-recessed text-text-secondary" : "",
              ].join(" ")}
              data-testid={`grid-col-header-${col.key}`}
              data-is-today={isToday ? "true" : "false"}
            >
              <div>
                {MONTH_NAME[col.month - 1]} {col.year % 100 < 10
                  ? `0${col.year % 100}`
                  : col.year % 100}
              </div>
              <div className="text-[10px] text-text-tertiary mt-0.5">
                {col.cells.reduce((sum, c) => sum + c.count, 0)} opps
              </div>
            </div>
          );
        })}

        {/* Stage rows */}
        {activeStages.map((stage, rowIdx) => (
          <Row
            key={stage}
            stage={stage}
            rowIdx={rowIdx}
            totalStages={activeStages.length}
            board={board}
            todayKey={todayKey}
            onOpportunityClick={onOpportunityClick}
            onCountTileClick={onCountTileClick}
            onDrop={onDrop}
          />
        ))}
      </div>
    </div>
  );
}


function Row({
  stage, rowIdx, totalStages, board, todayKey,
  onOpportunityClick, onCountTileClick, onDrop,
}: {
  stage: OpportunityStage;
  rowIdx: number;
  totalStages: number;
  board: RenewalBoardResponse;
  todayKey: string;
  onOpportunityClick: (id: string) => void;
  onCountTileClick: (t: { month: string; stage: string }) => void;
  onDrop: Props["onDrop"];
}) {
  // FE-PINV-068: per-stage `{n} active` subhead = sum of counts in
  // this stage's row across every visible month column.
  const activeCount = board.columns.reduce((sum, col) => {
    const cell = col.cells.find((c) => c.stage === stage);
    return sum + (cell?.count ?? 0);
  }, 0);
  return (
    <>
      <div
        className={[
          "px-2 py-2",
          "flex flex-col justify-center",
          "border-r border-border-subtle",
          // Sticky-left pins the stage label as the producer scrolls
          // horizontally through the month axis. ``bg-canvas`` hides
          // cards sliding underneath; z-10 sits below the column-
          // header ribbon (z-20) and below the corner (z-30) so the
          // intersection layering stays correct.
          //
          // No ``self-start`` here: the row header must stretch to
          // the row's full height so its ``bg-canvas`` covers every
          // pixel of the lead column. With ``self-start`` the bg
          // only filled the label's natural height — body cards
          // sliding under the row showed through the empty area
          // below.
          "sticky left-0 z-10 bg-canvas",
        ].join(" ")}
        data-testid={`grid-row-header-${stage}`}
      >
        <span className="text-[11px] uppercase tracking-wide font-medium text-text-secondary">
          {STAGE_LABEL[stage]}
        </span>
        <span
          data-testid={`grid-row-active-count-${stage}`}
          className="text-[10px] text-text-tertiary"
        >
          {activeCount} active
        </span>
      </div>
      {board.columns.map((col, colIdx) => {
        const cell = col.cells.find((c) => c.stage === stage);
        const isLastCol = colIdx === board.columns.length - 1;
        if (!cell) return (
          <div
            key={col.key}
            className={isLastCol ? "" : "border-r border-border-subtle"}
          />
        );

        // Diagonal cadence shading per spec: top-left (early stage,
        // imminent renewal) is urgent; bottom-right (late stage,
        // far-future renewal) is the relaxed corner. We render a
        // faint diagonal gradient by computing a normalized
        // diagonal position and applying alternating background
        // intensity.
        const onDiagonal = Math.abs(
          (colIdx / Math.max(1, board.columns.length - 1))
          - (rowIdx / Math.max(1, totalStages - 1))
        ) < 0.18;

        const isTodayCol = col.key === todayKey;
        return (
          <div
            key={`${col.key}-${stage}`}
            data-testid={`grid-cell-${col.key}-${stage}`}
            data-render-mode={cell.card_render_hint}
            data-today-column={isTodayCol ? "true" : undefined}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            }}
            onDrop={(e) => {
              e.preventDefault();
              const raw = e.dataTransfer.getData(
                "application/x-renewal-board-opp",
              );
              if (!raw) return;
              try {
                const parsed = JSON.parse(raw);
                onDrop({
                  opportunity_id: parsed.opportunity_id,
                  from: {
                    stage: parsed.stage,
                    renewal_year: parsed.renewal_year,
                    renewal_month: parsed.renewal_month,
                  },
                  to: {
                    stage,
                    renewal_year: col.year,
                    renewal_month: col.month,
                  },
                });
              } catch {
                // Bad payload — ignore. Browser DnD interop can be
                // fragile; we'd rather silently skip than crash.
              }
            }}
            className={[
              // Cell fills its full grid-row track so the drop target
              // covers the entire visual swimlane. Previously we used
              // ``self-start`` to hug content (so a sparse cell in a
              // tall row didn't stretch its single card), but that
              // left ungoverned empty space below short cells — drops
              // there silently fell through to the grid container.
              // CountTile's ``h-full`` was the original reason for
              // self-start; CountTile no longer uses h-full, so cells
              // can safely stretch without making single tiles
              // oversized.
              "min-h-[88px] p-2 flex flex-col gap-1",
              "bg-canvas",
              isLastCol ? "" : "border-r border-border-subtle",
              onDiagonal ? "bg-recessed/40" : "",
              isTodayCol ? "bg-teal/5" : "",
            ].join(" ")}
          >
            {cell.count === 0 ? null : cell.card_render_hint === "cards" ? (
              cell.opportunities.map((opp) => (
                <OpportunityCard
                  key={opp.opportunity_id}
                  opp={opp}
                  onClick={onOpportunityClick}
                />
              ))
            ) : (
              <CountTile
                cell={cell}
                monthKey={col.key}
                onClick={onCountTileClick}
              />
            )}
          </div>
        );
      })}
      {/* Row-spanning bottom divider. Drawn as its own grid item
          rather than ``border-b`` on each cell because cells use
          ``self-start`` (so they hug their content) — per-cell
          bottom borders would land at uneven heights across the
          row. This 1px element sits in its own auto-sized grid
          row and renders a uniform ruling across all columns,
          matching the pencil redesigned frame. */}
      <div
        aria-hidden="true"
        style={{ gridColumn: "1 / -1" }}
        className="border-b border-border-subtle"
      />
    </>
  );
}


